import Image from "next/image";
import {
  maxWidthContainer,
  fullWidthContainer,
} from "@/components/CourseContent/styles/constants";
import Link from "next/link";
import { PartialPartner } from "@/common/partner/types";
import { Program } from "@/common/program/types";
import { ProcessedCourse } from "@/common/course/types";
import { ProgramPricingData } from "@/lib/ecommerce/commercetools/client-only/getProgramPricingData";
import DynamicBadges from "./DynamicBadges";
import ProgramActionCard from "./ProgramActionCard";
import VerifiedBadge from "@/components/VerifiedBadge";
import HeroLaunchBadges from "@/components/HeroLaunchBadges";
import NewBadge from "@/components/NewBadge";
import { isProgramNew } from "@/utils/isDetailProductNew";
import getProgramDuration from "@/lib/program/utils/getProgramDuration";
import Breadcrumbs from "@/components/Breadcrumbs/Breadcrumbs";
import { useProgramRedesignConfig } from "@/monarch/rules/programRedesignTraffic/useProgramRedesignTraffic";
import CountdownBanner from "@/components/CountdownBanner";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import useFeatureFlag from "@/lib/feature-flags/hooks/useFeatureFlag";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import { ProgramRedesignTrafficTestResult } from "@/monarch/rules/programRedesignTraffic/types";
import EmailOptIn from "@/components/EmailOptIn/EmailOptIn";
import useEmailOptIn from "@/components/ProgramDetailLayout/templates/context/ProgramDetailContext/hooks/useEmailOptIn";
import FactoidBar from "@/components/CourseContent/ContentRefresh/FactoidBar";
import parse from "html-react-parser";
import { interpolateTemplateString } from "@/utils/functions/interpolateTemplateString";
import Ribbon from "@/components/Ribbon";
import HeroAccentCallout from "@/components/GeoPricing/HeroAccentCallout";
import useTranslations from "@/i18n/useTranslations";

export interface TaggedHeaderProps {
  programData: Program;
  courses: ProcessedCourse[];
  pricingData: Partial<ProgramPricingData> | null;
  variant?: "dark" | "light";
  skus: string[];
  isProgram?: boolean;
  simpleMobileHeader?: boolean;
  showDynamicBadges?: boolean;
  programIsRetired?: boolean;
}

const VARIANT_STYLES = {
  light: {
    bg: "bg-putty-100",
    text: "text-[#002626]",
    badge: "bg-putty-400 border-putty-700",
    badgeText: "text-black",
    border: "border-putty-400",
  },
  dark: {
    bg: "bg-primary-500",
    text: "text-white",
    badge: "bg-[#BEF4E5] border-[#004f4f]",
    badgeText: "text-primary-500",
    border: "border-[#003838]",
  },
};

const LOGO_SIZE_MAP = {
  sm: { width: 120, height: 60 },
  md: { width: 180, height: 90 },
  lg: { width: 240, height: 120 },
};
export default function TaggedHeader({
  courses,
  programData,
  pricingData,
  variant = "light",
  skus,
  isProgram = false,
  simpleMobileHeader = false,
  showDynamicBadges = true,
  programIsRetired = false,
}: TaggedHeaderProps) {
  const tCourse = useTranslations("Course");
  const {
    displayExperience,
    isLoading,
    factoidsBar,
    showSideRail,
    showHeaderFactoidBar,
    verifiedBadge,
    insertBadges,
    currencySelector,
    partnerLogo,
    showHeaderRibbon,
    headerRibbonText,
    headerBackgroundImageUrl,
    newBadge,
  } = useProgramRedesignConfig();

  const { emailOptIn, setEmailOptIn } = useEmailOptIn();
  const partners = programData.partners?.length ? programData.partners : [];
  const title = programData.title || "";
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.light;
  const breadcrumbVariant = variant === "light" ? "dark" : "light";
  const showSimpleMobileHeader =
    displayExperience === "valueOriented" && simpleMobileHeader;
  const backgroundStyle = headerBackgroundImageUrl
    ? {
        background: `linear-gradient(0deg, rgba(0, 0, 0, 0.65) 0%, rgba(0, 0, 0, 0.65) 100%), url(${headerBackgroundImageUrl}) lightgray 50% / cover no-repeat`,
      }
    : undefined;
  const isLogoAboveTitle = partnerLogo?.position === "aboveTitle";
  const isLogoAboveCta = partnerLogo?.position === "aboveCta";
  const logoSize = LOGO_SIZE_MAP[partnerLogo?.size ?? "md"];
  const logoElements = partners.map((partner: PartialPartner) => {
    const imageClassName = "max-w-full bg-white px-2 rounded-[8px] shadow-lg";
    const img = (
      <Image
        src={partner.logoUrl || ""}
        width={logoSize.width}
        height={logoSize.height}
        alt={title}
        title={title}
        key={partner.key}
        className={imageClassName}
      />
    );
    return partner.showOnSchoolsAndPartnersPage ? (
      <Link
        className="d-inline-block rounded-[8px]"
        key={partner.key}
        href={`/school/${partner.slug}`}
      >
        {img}
      </Link>
    ) : (
      <span key={partner.key}>{img}</span>
    );
  });

  const verifiedBadgeEl = verifiedBadge ? (
    <VerifiedBadge
      partners={partners}
      className={`${styles.badge} ${styles.badgeText}`}
      textClassName={styles.badgeText}
      verifiedBadgeConfig={verifiedBadge}
    />
  ) : null;
  const { flagValue: detailNewBadgeFlag } = useFeatureFlag(
    "NEXT_PUBLIC_FEATURE_DETAIL_NEW_BADGE",
    "legacy",
  );
  const showNewBadge =
    detailNewBadgeFlag === "test" &&
    isProgramNew(programData.created, undefined, newBadge?.windowDays);
  const badgesBesideTitle =
    isLogoAboveTitle && !showHeaderFactoidBar && showDynamicBadges;
  const launchBadges = (className?: string, includeNewBadge = true) => (
    <HeroLaunchBadges
      verifiedBadge={verifiedBadgeEl}
      showNewBadge={includeNewBadge && showNewBadge}
      className={className}
    />
  );

  const partnerKey = partners[0]?.key ?? title;

  const resolvedRibbonText = parse(
    interpolateTemplateString(headerRibbonText, {
      "{{partnerName}}": partnerKey,
      "{{courseName}}": programData.title || "",
    }),
  );

  return (
    <>
      <div
        className={`${styles.bg} ${styles.text} md:pb-8 md:pt-10 pt-12 pb-6 w-full`}
        style={backgroundStyle}
      >
        <div className={`${maxWidthContainer} ${fullWidthContainer} pt-4`}>
          <Breadcrumbs
            variant={breadcrumbVariant}
            path={programData.marketingUrl || ""}
            pageName={title}
            linksToShowOnSmallScreen={1}
            containerClassName="not-prose"
          />
        </div>

        <div
          className={`${maxWidthContainer} ${fullWidthContainer} gap-8 z-10`}
        >
          <div className="pb-3 pt-0 px-0 md:flex md:items-start md:justify-between transition-colors duration-300">
            <div className="flex flex-col md:flex-row gap-5 md:gap-10 items-start w-full">
              <div className="flex flex-col gap-5 flex-grow">
                <div className="flex flex-col">
                  {isLogoAboveTitle && (
                    <div className="flex justify-center md:justify-start items-center gap-2.5 py-4">
                      {!badgesBesideTitle && showNewBadge && <NewBadge />}
                      {logoElements}
                    </div>
                  )}
                  {isLogoAboveCta &&
                    launchBadges("flex flex-row gap-2 justify-start py-4")}
                  {!partnerLogo && (
                    <div className="flex flex-col md:flex-row md:justify-start items-start md:items-center gap-2.5 py-4">
                      {showNewBadge && <NewBadge />}
                      <div className="flex md:justify-start items-center gap-2.5 w-full md:w-auto">
                        {partners.map((partner: PartialPartner) => {
                          const logo = (
                            <div className="bg-white rounded-[8px] border border-[1px] border-[#E7E4DB] w-[120px] h-[56px] flex items-center justify-center">
                              <Image
                                src={partner.logoUrl || ""}
                                width={100}
                                height={40}
                                alt={title || ""}
                                title={title || ""}
                                key={partner.key}
                                className="object-contain max-h-full max-w-full"
                              />
                            </div>
                          );
                          return partner.showOnSchoolsAndPartnersPage ? (
                            <Link
                              className="d-inline-block"
                              key={partner.key}
                              href={`/school/${partner.slug}`}
                            >
                              {logo}
                            </Link>
                          ) : (
                            <span key={partner.key}>{logo}</span>
                          );
                        })}
                      </div>
                      {launchBadges(undefined, false)}
                      <FeatureFlagComponentResolverClient
                        flagKey="NEXT_PUBLIC_FEATURE_OC_COUNTDOWN_BANNER"
                        variants={{
                          test: (
                            <MonarchDataConsumer<ProgramRedesignTrafficTestResult>
                              experimentKey="useProgramRedesignTraffic"
                              render={(testResult) => {
                                const countdownBanner =
                                  testResult?.data?.countdownBanner;
                                return (
                                  <div className="mt-4 md:mt-0 md:ml-4 w-full md:w-auto">
                                    <CountdownBanner
                                      variant="micro"
                                      theme={
                                        countdownBanner?.hero?.theme || "light"
                                      }
                                      header={countdownBanner?.hero?.text || ""}
                                      targetDate={
                                        countdownBanner?.hero?.date || ""
                                      }
                                      htmlId="hero-program-promo-countdown-banner"
                                      location="program-detail-page"
                                      name="hero-program-promo-countdown-banner"
                                    />
                                  </div>
                                );
                              }}
                            />
                          ),
                          legacy: <></>,
                        }}
                        fallback="legacy"
                      />
                    </div>
                  )}

                  <h1
                    className={`m-0 p-0 tracking-tight font-black ${styles.text}`}
                  >
                    {title}
                  </h1>

                  {!showHeaderFactoidBar && (
                    <div
                      className={
                        !showSimpleMobileHeader ? "" : "hidden md:block"
                      }
                    >
                      {insertBadges && (
                        <DynamicBadges
                          courseData={courses}
                          styles={{
                            badge: styles.badge,
                            badgeText: styles.badgeText,
                          }}
                          insertBadges={insertBadges}
                        />
                      )}
                      {showDynamicBadges && (
                        <DynamicBadges
                          courseData={courses}
                          styles={{
                            badge: styles.badge,
                            badgeText: styles.badgeText,
                          }}
                          leading={
                            badgesBesideTitle && showNewBadge ? (
                              <NewBadge />
                            ) : null
                          }
                        >
                          {badgesBesideTitle ? verifiedBadgeEl : null}
                        </DynamicBadges>
                      )}
                    </div>
                  )}

                  {isProgram ? (
                    <div
                      className={`flex items-center gap-1 ${showHeaderFactoidBar ? "mt-6" : ""}`}
                    >
                      <a href="#courses">
                        <p
                          className={`font-normal ${styles.text} leading-[140%] underline decoration-solid m-0`}
                        >
                          {tCourse("coursesCount", {
                            count: programData.courses.length,
                          })}
                        </p>
                      </a>
                      <p
                        className={`font-normal ${styles.text} leading-[140%] m-0`}
                      >
                        , {getProgramDuration(courses)}
                      </p>
                    </div>
                  ) : null}

                  <HeroAccentCallout
                    pricingData={pricingData}
                    variant={variant}
                    className="mt-4"
                    location="program-detail-page"
                  />
                </div>
              </div>

              {/* CTA column */}
              <div className="flex w-full md:w-auto self-center">
                <div className="flex gap-4 flex-col w-full">
                  {/* Logo — overlapping the top of the CTA card */}
                  {isLogoAboveCta && (
                    <div className="flex justify-center items-center translate-y-1/2 relative z-10">
                      {logoElements}
                    </div>
                  )}
                  <ProgramActionCard
                    programData={programData}
                    courses={courses}
                    skus={skus}
                    variant={
                      displayExperience === "referral" ? "light" : variant
                    }
                    pricingData={pricingData}
                    programIsEnrollable={
                      programData.programEligibleForOneClickPurchase
                    }
                    programIsRetired={programIsRetired}
                    currencySelector={currencySelector}
                    isLogoAboveCta={isLogoAboveCta}
                  />
                </div>
              </div>

              {showSimpleMobileHeader && !showHeaderFactoidBar && (
                <div className="block md:hidden gap-3 flex flex-col">
                  {insertBadges && (
                    <DynamicBadges
                      courseData={courses}
                      styles={{
                        badge: styles.badge,
                        badgeText: styles.badgeText,
                      }}
                      insertBadges={insertBadges}
                    />
                  )}
                  {showDynamicBadges && (
                    <DynamicBadges
                      courseData={courses}
                      styles={{
                        badge: styles.badge,
                        badgeText: styles.badgeText,
                      }}
                      leading={
                        badgesBesideTitle && showNewBadge ? <NewBadge /> : null
                      }
                    >
                      {badgesBesideTitle ? verifiedBadgeEl : null}
                    </DynamicBadges>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className={`${maxWidthContainer} ${fullWidthContainer} pt-6 px-0 mb-2 flex gap-6`}
        >
          {courses && courses.length > 0 && showHeaderFactoidBar && (
            <div className="w-full block md:hidden">
              <FactoidBar
                courseData={courses}
                isFactoidsBarFieldsLoading={isLoading}
                monarchFactoidsBar={factoidsBar}
                isHiddingLastElement={showSideRail}
                isHeaderFactoidBar={true}
              />
            </div>
          )}
          <EmailOptIn
            optIn={emailOptIn}
            partners={partners}
            title={title}
            toggleEmailOptIn={() => {
              setEmailOptIn(!emailOptIn);
            }}
            variant="secondary"
            labelClassName={`${styles.text} text-xs md:text-sm`}
          />
          {courses && courses.length > 0 && showHeaderFactoidBar && (
            <div className="w-full hidden md:block">
              <FactoidBar
                courseData={courses}
                isFactoidsBarFieldsLoading={isLoading}
                monarchFactoidsBar={factoidsBar}
                isHiddingLastElement={showSideRail}
                isHeaderFactoidBar={true}
              />
            </div>
          )}
        </div>
      </div>

      {showHeaderRibbon && <Ribbon>{resolvedRibbonText}</Ribbon>}
    </>
  );
}
